import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import PersonSelectorTransfer from '../common/formComponents/PersonSelectorTransfer.js';
import {
  getOnCallAdminData,
  saveOnCallReportScope,
  saveOnCallSlt,
  saveOnCallTypes,
} from './sysManagerOnCallApi.js';
import './SysManagerOnCall.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const REPORT_SELECT_STYLES = {
  menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

/** Create a stable client-side ID for a new call type. */
function createCallTypeId() {
  return window.crypto?.randomUUID?.() || `oncall-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Return an editable call type with safe visual defaults. */
function createCallType() {
  return {
    id: createCallTypeId(),
    typeName: '',
    colourCode: '#f9b233',
    fontColour: '#ffffff',
  };
}

/** Return a readable avatar fallback when an image cannot be displayed. */
function getStaffInitial(displayName) {
  return String(displayName || '?').trim().charAt(0).toUpperCase() || '?';
}

/** System Manager editor for OnCall types and assignable SLT staff. */
export default function SysManagerOnCall() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingTypes, setSavingTypes] = useState(false);
  const [savingStaff, setSavingStaff] = useState(false);
  const [callTypes, setCallTypes] = useState([]);
  const [activeStaff, setActiveStaff] = useState([]);
  const [pupilGroups, setPupilGroups] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [savingScopeCogId, setSavingScopeCogId] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);

  const selectedStaff = useMemo(() => {
    const selectedIdSet = new Set(selectedStaffIds);
    return activeStaff.filter((staffMember) => selectedIdSet.has(String(staffMember.cogId)));
  }, [activeStaff, selectedStaffIds]);

  const filteredReportStaff = useMemo(() => {
    const cleanSearch = staffSearch.trim().toLowerCase();
    if (!cleanSearch) {
      return activeStaff;
    }
    return activeStaff.filter((staffMember) => (
      String(staffMember.displayName || '').toLowerCase().includes(cleanSearch)
      || String(staffMember.email || '').toLowerCase().includes(cleanSearch)
    ));
  }, [activeStaff, staffSearch]);

  const reportOptionGroups = useMemo(() => [
    {
      label: 'Whole school',
      options: [{ value: 'wholeSchool', label: 'View Whole School', type: 'wholeSchool' }],
    },
    ...pupilGroups.map((group) => ({
      label: group.groupName,
      options: [
        {
          value: `group:${encodeURIComponent(group.groupName)}`,
          label: `Entire group: ${group.groupName}`,
          type: 'group',
          scopeValue: group.groupName,
        },
        ...(group.classes || []).map((className) => ({
          value: `class:${encodeURIComponent(group.groupName)}|${encodeURIComponent(className)}`,
          label: `Class: ${className}`,
          type: 'class',
          groupName: group.groupName,
          scopeValue: className,
        })),
      ],
    })),
  ], [pupilGroups]);

  const reportOptionsByValue = useMemo(() => {
    const options = reportOptionGroups.flatMap((optionGroup) => optionGroup.options);
    return new Map(options.map((option) => [option.value, option]));
  }, [reportOptionGroups]);

  useEffect(() => {
    const permissions = userAppData?.sysManager || {};
    if (Number(permissions.perms ?? 0) !== 1 || permissions.onCallAdmin !== true) {
      navigate('/app');
      return;
    }
    loadSettings();
  }, [navigate]);

  /** Load both editable settings and build the selector's selected-ID list. */
  async function loadSettings() {
    setLoading(true);
    try {
      const response = await getOnCallAdminData();
      setCallTypes(response.onCallType);
      setActiveStaff(response.activeStaff);
      setPupilGroups(response.pupilGroups);
      setSelectedStaffIds(response.onCallSlt
        .filter((staffMember) => staffMember.active !== false)
        .map((staffMember) => String(staffMember.cogId || '').trim())
        .filter(Boolean));
    } catch (error) {
      console.error('Failed to load OnCall settings', error);
      toast.error('❌ Failed to load OnCall settings.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  /** Convert stored backend selections into current react-select options. */
  function getSelectedReportOptions(staffMember) {
    const selections = Array.isArray(staffMember.reportSelections)
      ? staffMember.reportSelections
      : [];
    return selections.map((selection) => {
      if (selection.type === 'wholeSchool') {
        return reportOptionsByValue.get('wholeSchool');
      }
      if (selection.type === 'group') {
        return reportOptionsByValue.get(`group:${encodeURIComponent(selection.value)}`);
      }
      if (selection.type === 'class') {
        return reportOptionsByValue.get(`class:${encodeURIComponent(selection.groupName)}|${encodeURIComponent(selection.value)}`);
      }
      return null;
    }).filter(Boolean);
  }

  /** Persist multiple report scopes immediately and update only the affected row. */
  async function handleReportScopeChange(staffMember, selectedOptions, actionMeta) {
    const targetCogId = String(staffMember.cogId || '').trim();
    let cleanOptions = Array.isArray(selectedOptions) ? selectedOptions : [];
    if (actionMeta?.option?.type === 'wholeSchool') {
      cleanOptions = cleanOptions.filter((option) => option.type === 'wholeSchool');
    } else if (actionMeta?.option && actionMeta.option.type !== 'wholeSchool') {
      cleanOptions = cleanOptions.filter((option) => option.type !== 'wholeSchool');
    }
    const selections = cleanOptions.map((option) => ({
      type: option.type,
      value: option.type === 'wholeSchool' ? 'wholeSchool' : option.scopeValue,
      ...(option.type === 'class' ? { groupName: option.groupName } : {}),
    }));
    setSavingScopeCogId(targetCogId);
    try {
      const response = await saveOnCallReportScope({ targetCogId, selections });
      const canViewWholeSchoolData = response?.effectivePermissions?.onCallData?.canViewWholeSchoolData === true;
      setActiveStaff((currentStaff) => currentStaff.map((currentMember) => (
        currentMember.cogId === targetCogId
          ? {
            ...currentMember,
            reportSelections: Array.isArray(response.onCallReportSelections)
              ? response.onCallReportSelections
              : selections,
            reportGroups: Array.isArray(response.onCallReportGroups) ? response.onCallReportGroups : [],
            canViewWholeSchoolData,
          }
          : currentMember
      )));
      toast.success('✅ OnCall report access saved.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to save OnCall report access', error);
      toast.error('❌ Failed to save OnCall report access.', TOAST_CONFIG);
    } finally {
      setSavingScopeCogId('');
    }
  }

  /** Apply one local field change without replacing the type's stable ID. */
  function updateCallType(typeId, fieldName, value) {
    setCallTypes((currentTypes) => currentTypes.map((callType) => (
      callType.id === typeId ? { ...callType, [fieldName]: value } : callType
    )));
  }

  /** Save the complete type list after basic client-side name validation. */
  async function handleSaveTypes() {
    if (callTypes.some((callType) => !String(callType.typeName || '').trim())) {
      toast.error('❌ Every OnCall type needs a name.', TOAST_CONFIG);
      return;
    }
    setSavingTypes(true);
    try {
      const response = await saveOnCallTypes(callTypes);
      setCallTypes(Array.isArray(response.onCallType) ? response.onCallType : callTypes);
      toast.success('✅ OnCall types saved.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to save OnCall types', error);
      toast.error('❌ Failed to save OnCall types.', TOAST_CONFIG);
    } finally {
      setSavingTypes(false);
    }
  }

  /** Save only active Cognito IDs selected in the shared staff transfer control. */
  async function handleSaveStaff() {
    setSavingStaff(true);
    try {
      const response = await saveOnCallSlt(selectedStaffIds);
      setSelectedStaffIds(Array.isArray(response.onCallSlt) ? response.onCallSlt : selectedStaffIds);
      toast.success('✅ OnCall SLT staff saved.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to save OnCall SLT staff', error);
      toast.error('❌ Failed to save OnCall SLT staff.', TOAST_CONFIG);
    } finally {
      setSavingStaff(false);
    }
  }

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_onCall_container'>
        <Header
          title='OnCall Settings'
          subtitle='Manage the call types and staff available for OnCall assignment.'
          rightContent={(
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}>
              <FaArrowLeft aria-hidden='true' />
              Back to System Manager
            </button>
          )}
        />

        {loading ? (
          <div className='sysManager_onCall_status'>Loading OnCall settings...</div>
        ) : (
          <div className='sysManager_onCall_sections'>
            <section className='sysManager_onCall_card'>
              <div className='sysManager_onCall_sectionHeader'>
                <div>
                  <h2>Call types</h2>
                  <p>Set the labels and colours shown throughout the OnCall module.</p>
                </div>
                <button className='sysManager_onCall_secondaryButton' type='button' onClick={() => setCallTypes((current) => [...current, createCallType()])}>
                  <FaPlus aria-hidden='true' /> Add type
                </button>
              </div>

              <div className='sysManager_onCall_typeList'>
                {callTypes.length === 0 && <p className='sysManager_onCall_empty'>No call types are configured.</p>}
                {callTypes.map((callType) => (
                  <div className='sysManager_onCall_typeRow' key={callType.id}>
                    <label>
                      <span>Type name</span>
                      <input type='text' value={callType.typeName || ''} onChange={(event) => updateCallType(callType.id, 'typeName', event.target.value)} />
                    </label>
                    <label>
                      <span>Background</span>
                      <input type='color' value={callType.colourCode || '#ffffff'} onChange={(event) => updateCallType(callType.id, 'colourCode', event.target.value)} />
                    </label>
                    <label>
                      <span>Text</span>
                      <input type='color' value={callType.fontColour || '#706f6f'} onChange={(event) => updateCallType(callType.id, 'fontColour', event.target.value)} />
                    </label>
                    <div className='sysManager_onCall_preview' style={{ backgroundColor: callType.colourCode, color: callType.fontColour }}>
                      {callType.typeName || 'Preview'}
                    </div>
                    <button className='sysManager_onCall_deleteButton' type='button' aria-label={`Remove ${callType.typeName || 'call type'}`} onClick={() => setCallTypes((current) => current.filter((item) => item.id !== callType.id))}>
                      <FaTrash aria-hidden='true' />
                    </button>
                  </div>
                ))}
              </div>
              <div className='sysManager_onCall_actions'>
                <button className='sysManager_onCall_primaryButton' type='button' disabled={savingTypes} onClick={handleSaveTypes}>
                  {savingTypes ? 'Saving...' : 'Save call types'}
                </button>
              </div>
            </section>

            <section className='sysManager_onCall_card'>
              <div className='sysManager_onCall_sectionHeader'>
                <div>
                  <h2>OnCall SLT staff</h2>
                  <p>Select the active staff who should be available for OnCall assignment.</p>
                </div>
              </div>
              <PersonSelectorTransfer
                allPeople={activeStaff}
                selectedPeople={selectedStaff}
                onChange={(people) => setSelectedStaffIds(people.map((person) => person.cogId).filter(Boolean))}
                allTitle='Available staff'
                selectedTitle='OnCall SLT staff'
                enableLeftSearch
                leftSearchPlaceholder='Search staff'
                disabled={savingStaff}
              />
              <div className='sysManager_onCall_actions'>
                <button className='sysManager_onCall_primaryButton' type='button' disabled={savingStaff} onClick={handleSaveStaff}>
                  {savingStaff ? 'Saving...' : 'Save SLT staff'}
                </button>
              </div>
            </section>

            <section className='sysManager_onCall_card'>
              <div className='sysManager_onCall_sectionHeader'>
                <div>
                  <h2>Report access</h2>
                  <p>Choose the class, pupil group, or whole-school reports each active staff member can view.</p>
                </div>
              </div>
              <div className='sysManager_onCall_reportToolbar'>
                <input
                  type='search'
                  value={staffSearch}
                  placeholder='Search staff...'
                  aria-label='Search staff report access'
                  onChange={(event) => setStaffSearch(event.target.value)}
                />
              </div>
              <div className='sysManager_onCall_reportTableWrap'>
                <table className='sysManager_onCall_reportTable'>
                  <thead>
                    <tr>
                      <th>Staff member</th>
                      <th>Report scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReportStaff.map((staffMember) => {
                      const cogId = String(staffMember.cogId || '').trim();
                      const isSaving = savingScopeCogId === cogId;
                      return (
                        <tr key={cogId}>
                          <td>
                            <div className='sysManager_onCall_staffCell'>
                              <div className='sysManager_onCall_avatarWrap'>
                                <div className='sysManager_onCall_avatarFallback'>
                                  {getStaffInitial(staffMember.displayName)}
                                </div>
                                {staffMember.profileImageUrl && (
                                  <img
                                    className='sysManager_onCall_avatar'
                                    src={staffMember.profileImageUrl}
                                    alt=''
                                    onError={(event) => {
                                      event.currentTarget.style.opacity = '0';
                                    }}
                                  />
                                )}
                              </div>
                              <div>
                                <strong>{staffMember.displayName || 'Unknown user'}</strong>
                                {staffMember.email && <span>{staffMember.email}</span>}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='sysManager_onCall_reportSelectRow'>
                              <Select
                                className='sysManager_onCall_reportSelect'
                                classNamePrefix='sysManager-oncall-report-select'
                                isMulti
                                isClearable
                                closeMenuOnSelect={false}
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                                styles={REPORT_SELECT_STYLES}
                                options={reportOptionGroups}
                                value={getSelectedReportOptions(staffMember)}
                                isDisabled={Boolean(savingScopeCogId)}
                                placeholder='Select classes or groups...'
                                noOptionsMessage={() => 'No report scopes found'}
                                aria-label={`Report scope for ${staffMember.displayName || 'staff member'}`}
                                onChange={(options, actionMeta) => handleReportScopeChange(staffMember, options, actionMeta)}
                              />
                              {isSaving && <span className='sysManager_onCall_savingText'>Saving...</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredReportStaff.length === 0 && (
                  <p className='sysManager_onCall_empty'>No staff matched your search.</p>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
